import randomSample from "lodash/sample";

import { hasOwnProperty, definedMap, withDefault } from "collection-utils";

import type {
    TableGlideType,
    TableColumn,
    TableRefGlideType,
    Description,
    PropertySource,
    TypeSchema,
    InputOutputTables,
    PropertyDescription,
    LegacyPropertyDescription,
} from "../description";
import { PropertyKind, makeTableRef } from "../description";
import {
    getColumnProperty,
    makeColumnProperty,
    getTableProperty,
    getStringProperty,
    makeStringProperty,
    getSwitchProperty,
    makeSwitchProperty,
    makeTableProperty,
    getEnumProperty,
    makeEnumProperty,
    getZapProperty,
    getNumberProperty,
    makeNumberProperty,
} from "./property-utils";
import type {
    PropertyDescriptor,
    PropertyTableGetter,
    ColumnTypePredicate,
    ColumnPropertyDescriptor,
    TablePropertyDescriptor,
    StringPropertyDescriptor,
    SwitchPropertyDescriptor,
    EnumPropertyDescriptor,
    EnumPropertyCase,
    EnumVisual,
    SchemaInspector,
    ZapPropertyDescriptor,
    NumberPropertyDescriptor,
    NumberPropertyStyle,
    MultiCasePropertyDescriptor,
    Subcomponent,
} from ".";
import { isMultiCasePropertyDescriptor, RequiredKind } from ".";
import type { TableName } from "../../database-strings";
import { ImageAspectRatio } from "./image-types";
import type { SuperPropertySection } from "./property-sections";
import { PropertySection } from "./property-sections";
import type { BasePrimitiveValue } from "../../components/computation-types";
import { defined } from "../../Support";
import { assert } from "@glideapps/ts-necessities";

class PropertyHandler<TKind extends PropertyKind, TDesc extends Description> {
    constructor(private readonly _kind: TKind, protected readonly name: string, public readonly label: string) {}

    public get kind(): TKind {
        return this._kind;
    }

    public get property(): PropertySource<TDesc> {
        return { name: this.name };
    }
}

export enum ColumnPropertyFlag {
    // "optional" is ignored.  It's only here to do `flag ? "required" : "optional"`
    Optional,
    Required,
    Editable,
    Searchable,
    Legacy,
    EmptyByDefault,
    DefaultCaption,
    EditedInApp,
    AllowUserProfileColumns,
}

export class ColumnPropertyHandler<TDesc extends Description>
    extends PropertyHandler<PropertyKind.Column, TDesc>
    implements ColumnPropertyDescriptor<TDesc>
{
    constructor(
        name: string,
        label: string,
        private readonly _flags: ReadonlyArray<ColumnPropertyFlag>,
        public readonly getIndirectTable: PropertyTableGetter<TDesc> | undefined,
        public readonly preferredNames: ReadonlyArray<string> | undefined,
        public readonly getAllowedColumns: (
            table: TableGlideType,
            desc: TDesc,
            schema: SchemaInspector
        ) => ReadonlyArray<TableColumn>,
        public readonly preferredType: ColumnTypePredicate,
        public readonly section: SuperPropertySection
    ) {
        super(PropertyKind.Column, name, label);
    }

    public get required(): boolean {
        return this._flags.indexOf(ColumnPropertyFlag.Required) >= 0;
    }

    public get editable(): boolean {
        return this._flags.indexOf(ColumnPropertyFlag.Editable) >= 0;
    }

    public get searchable(): boolean {
        return this._flags.indexOf(ColumnPropertyFlag.Searchable) >= 0;
    }

    public get isLegacy(): boolean {
        return this._flags.indexOf(ColumnPropertyFlag.Legacy) >= 0;
    }

    public get emptyByDefault(): boolean {
        return this._flags.indexOf(ColumnPropertyFlag.EmptyByDefault) >= 0;
    }

    public get isDefaultCaption(): boolean {
        return this._flags.indexOf(ColumnPropertyFlag.DefaultCaption) >= 0;
    }

    public get isEditedInApp(): boolean {
        return this._flags.indexOf(ColumnPropertyFlag.EditedInApp) >= 0;
    }

    public get allowUserProfileColumns(): boolean {
        return this._flags.indexOf(ColumnPropertyFlag.AllowUserProfileColumns) >= 0;
    }

    public getProperty(desc: TDesc): LegacyPropertyDescription | undefined {
        return (desc as any)[this.name];
    }

    public getColumnName(desc: TDesc): string | undefined {
        return getColumnProperty(this.getProperty(desc));
    }

    public setInDescription(desc: TDesc, columnName: string): TDesc {
        desc = { ...desc };
        (desc as any)[this.name] = makeColumnProperty(columnName);
        return desc;
    }
}

export class TablePropertyHandler<TDesc extends Description>
    extends PropertyHandler<PropertyKind.Table, TDesc>
    implements TablePropertyDescriptor<TDesc>
{
    constructor(
        name: string,
        label: string,
        public readonly required: boolean,
        public readonly getAllowedTables: (
            desc: TDesc,
            schema: TypeSchema,
            componentTables: InputOutputTables | undefined
        ) => ReadonlyArray<TableGlideType>,
        public readonly section: PropertySection
    ) {
        super(PropertyKind.Table, name, label);
    }

    public getTableName(desc: TDesc): TableName | undefined {
        return getTableProperty((desc as any)[this.name]);
    }

    public getTableRef(desc: TDesc): TableRefGlideType | undefined {
        return definedMap(this.getTableName(desc), makeTableRef);
    }

    public lookupTable(desc: TDesc, schema: SchemaInspector): TableGlideType | undefined {
        return definedMap(this.getTableRef(desc), ref => schema.findTable(ref));
    }

    public setInDescription(desc: TDesc, t: TableName): TDesc {
        desc = { ...desc };
        (desc as any)[this.name] = makeTableProperty(t);
        return desc;
    }
}

export class StringPropertyHandler<TDesc extends Description>
    extends PropertyHandler<PropertyKind.String, TDesc>
    implements StringPropertyDescriptor<TDesc>
{
    constructor(
        name: string,
        label: string,
        public readonly placeholder: string,
        public readonly required: boolean,
        public readonly isCaption: string | undefined,
        public readonly section: PropertySection
    ) {
        super(PropertyKind.String, name, label);
    }

    public getString(desc: TDesc): string | undefined {
        return getStringProperty((desc as any)[this.name]);
    }

    public setInDescription(desc: TDesc, str: string): TDesc {
        desc = { ...desc };
        (desc as any)[this.name] = makeStringProperty(str);
        return desc;
    }
}

export class NumberPropertyHandler<TDesc extends Description, TObject extends Record<string, number>>
    extends PropertyHandler<PropertyKind.Number, TDesc>
    implements NumberPropertyDescriptor<TDesc>
{
    public readonly defaultValue: number;
    constructor(
        defaultObject: TObject,
        label: string,
        public readonly placeholder: string,
        public readonly required: RequiredKind,
        public readonly style: NumberPropertyStyle,
        public readonly section: PropertySection,
        public readonly position?: number
    ) {
        super(PropertyKind.Number, defined(Object.keys(defaultObject)[0]), label);

        this.defaultValue = defaultObject[this.name];
    }

    public getNumber(desc: TDesc | undefined): number | undefined {
        const value = getNumberProperty((desc as any)?.[this.name]);
        if (value !== undefined) return value;
        if (this.required !== RequiredKind.NotRequiredDefaultMissing) return this.defaultValue;
        return undefined;
    }

    public setInDescription<T extends TDesc>(desc: T, n: number): T {
        desc = { ...desc };
        (desc as any)[this.name] = makeNumberProperty(n);
        return desc;
    }
}

export class SwitchPropertyHandler<TDesc extends Description, TObject extends Record<string, boolean>>
    extends PropertyHandler<PropertyKind.Switch, TDesc>
    implements SwitchPropertyDescriptor<TDesc>
{
    public readonly defaultValue: boolean;
    constructor(
        defaultObject: TObject,
        label: string,
        public readonly section: SuperPropertySection,
        public readonly subcomponent?: Subcomponent
    ) {
        super(PropertyKind.Switch, defined(Object.keys(defaultObject)[0]), label);

        this.defaultValue = defaultObject[this.name];
    }

    public getSwitch(desc: TDesc | undefined): boolean {
        if (desc === undefined) return this.defaultValue;
        return withDefault(getSwitchProperty((desc as any)[this.name]), this.defaultValue);
    }

    public getRandom(): boolean {
        return defined(randomSample([false, true]));
    }

    public getProperty(value: boolean): { [K in keyof TObject]: PropertyDescription } {
        return { [this.name]: makeSwitchProperty(value) } as {
            [K in keyof TObject]: PropertyDescription;
        };
    }

    public get default(): { [K in keyof TObject]: PropertyDescription } {
        return this.getProperty(this.defaultValue);
    }
}

export class EnumPropertyHandler<
        TDesc extends Description,
        P extends BasePrimitiveValue,
        TObject extends Record<string, P>
    >
    extends PropertyHandler<PropertyKind.Enum, TDesc>
    implements EnumPropertyDescriptor<P, TDesc>
{
    public readonly defaultCaseValue: P;

    constructor(
        defaultObject: TObject,
        label: string,
        public readonly menuLabel: string,
        public readonly cases: readonly EnumPropertyCase<P>[],
        public readonly section: SuperPropertySection,
        public readonly visual: EnumVisual,
        public readonly getIndirectTable?: PropertyTableGetter<TDesc>,
        public readonly subcomponent?: Subcomponent,
        public readonly isSearchable?: boolean
    ) {
        super(PropertyKind.Enum, defined(Object.keys(defaultObject)[0]), label);

        this.defaultCaseValue = defaultObject[this.name];
    }

    public getEnum(desc: TDesc): P {
        return withDefault(getEnumProperty((desc as any)[this.name]), this.defaultCaseValue);
    }

    public getRandom(): P {
        return defined(randomSample(this.cases)).value;
    }

    public getProperty(value: P): { [K in keyof TObject]: PropertyDescription } {
        return { [this.name]: makeEnumProperty(value) } as {
            [K in keyof TObject]: PropertyDescription;
        };
    }

    public get default(): { [K in keyof TObject]: PropertyDescription } {
        return this.getProperty(this.defaultCaseValue);
    }

    public setInDescription(desc: TDesc, value: P): TDesc {
        return { ...desc, [this.name]: makeEnumProperty(value) };
    }

    public withGetIndirectTable(
        getIndirectTable: PropertyTableGetter<TDesc> | undefined
    ): EnumPropertyHandler<TDesc, P, TObject> {
        assert(this.getIndirectTable === undefined);

        if (getIndirectTable === undefined) return this;

        const defaultObject = { [this.name]: this.defaultCaseValue } as TObject;
        return new EnumPropertyHandler(
            defaultObject,
            this.label,
            this.menuLabel,
            this.cases,
            this.section,
            this.visual,
            getIndirectTable
        );
    }
}

export class ZapPropertyHandler<TDesc extends Description>
    extends PropertyHandler<PropertyKind.Zap, TDesc>
    implements ZapPropertyDescriptor<TDesc>
{
    constructor(name: string, label: string, public readonly section: PropertySection) {
        super(PropertyKind.Zap, name, label);
    }

    public getZap(desc: TDesc): string | undefined {
        return getZapProperty((desc as any)[this.name]);
    }
}

export const isRequiredPropertyHandler = new SwitchPropertyHandler(
    { isRequired: false },
    "Required",
    PropertySection.Options
);

function getDefaultCaption(descr: PropertyDescriptor): string | undefined {
    if (!hasOwnProperty(descr.property, "name")) return undefined;

    if (isMultiCasePropertyDescriptor(descr)) {
        for (const c of descr.cases) {
            if (c.kind === PropertyKind.String) {
                return c.isCaption;
            }
        }
    } else if (descr.kind === PropertyKind.String) {
        return descr.isCaption;
    } else {
        return undefined;
    }
    return undefined;
}

export type CaptionPropertyDescriptor<T extends Description> =
    | StringPropertyDescriptor<T>
    | MultiCasePropertyDescriptor<T>;

export function findCaptionDescriptor<T extends Description>(
    properties: ReadonlyArray<PropertyDescriptor>
): CaptionPropertyDescriptor<T> | undefined {
    return properties.find(d => getDefaultCaption(d) !== undefined) as CaptionPropertyDescriptor<T> | undefined;
}

export function makeImageHeightPropertyHandler(
    defaultCaseValue: ImageAspectRatio,
    includeFit: boolean,
    includeCircle: boolean,
    includeExtendedOptions: boolean,
    label?: string
): EnumPropertyHandler<Description, ImageAspectRatio, { size: ImageAspectRatio }> {
    const cases: EnumPropertyCase<ImageAspectRatio>[] = [
        {
            value: ImageAspectRatio.Square,
            label: "Square",
            icon: "borderSquare",
        },
        {
            value: ImageAspectRatio.ThreeByOne,
            label: "3:1",
            icon: "border3by1",
        },
        {
            value: ImageAspectRatio.ThreeByTwo,
            label: "3:2",
            icon: "border3by2",
        },
    ];
    if (includeCircle) {
        cases.push({ value: ImageAspectRatio.Circle, label: "Circle", icon: "borderCircle" });
    }

    if (includeExtendedOptions) {
        cases.push(
            {
                value: ImageAspectRatio.FourByThree,
                label: "4:3",
                icon: "border4by3",
            },
            {
                value: ImageAspectRatio.TwoByThree,
                label: "2:3 (Vertical)",
                icon: "border2by3",
            },
            {
                value: ImageAspectRatio.ThreeByFour,
                label: "3:4 (Vertical)",
                icon: "border3by4",
            }
        );
    }
    if (includeFit) {
        cases.push({
            value: ImageAspectRatio.Fit,
            label: "Original",
            icon: "borderOriginal",
        });
    }

    return new EnumPropertyHandler(
        { size: defaultCaseValue },
        withDefault(label, "Size"),
        "Image height",
        cases,
        PropertySection.Design,
        includeExtendedOptions ? "dropdown" : "small-images"
    );
}

export const visibilityPropertyDescriptor: PropertyDescriptor = {
    kind: PropertyKind.Transforms,
    property: { name: "visibilityFilters" },
    // FIXME: This label is not used
    label: "Show component",
    addText: "Add Condition",
    description: "Set conditions for when the component should be visible.",
    specPrefix: "Show component when",
    allowContextTable: true,
    allowLHSUserProfileColumns: true,
    getIndirectTable: tables => tables.output,
    section: PropertySection.Visibility,
    withContainingScreen: false,
};
